/**
 * Today's notebook, beside the chart.
 *
 * The Daily screen is where a session is planned and reviewed. This is the
 * same entry, reachable without leaving the chart — which is the whole
 * co-pilot argument: a note written at the moment it occurs to somebody is a
 * different note from one reconstructed at five o'clock.
 *
 * ## It writes to the same row the Daily screen edits
 *
 * Not a separate scratchpad. A second place to write notes is a second place
 * to lose them, and a trader who typed something here and could not find it in
 * the review would be right to stop trusting either.
 *
 * ## Saving is explicit
 *
 * Deliberately not autosaved. Everything else in this app that saves behind
 * the trader — the layout, the watchlist — is a preference they can re-set in
 * a second. A half-written thought overwriting a finished one is not
 * recoverable, and the button costs a click.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { NotebookEntryDto } from '@aegis/api-client';
import { api } from '../lib/api.js';

/** The trading day this panel writes to, in the trader's own local day. */
function today(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function NotesPanel(): JSX.Element {
  const [entry, setEntry] = useState<NotebookEntryDto | null>(null);
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const day = today();

  useEffect(() => {
    let live = true;
    void api.dailyEntries(day, day)
      .then((r) => {
        if (!live) return;
        const prep = r.entries.find((e) => e.kind === 'prep') ?? null;
        setEntry(prep);
        setBody(prep?.body ?? '');
        setDirty(false);
      })
      .catch(() => { if (live) setError('Could not load today’s note.'); });
    return () => { live = false; };
  }, [day]);

  const save = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = entry
        ? await api.updateEntry(entry.id, { body })
        : await api.createEntry({ day, kind: 'prep', body });
      setEntry(saved);
      setDirty(false);
    } catch {
      // Left dirty on purpose. Clearing the flag would make an unsaved note
      // look saved, which is the one outcome worth avoiding here.
      setError('That did not save. Your text is still here — try again.');
    } finally {
      setBusy(false);
    }
  }, [entry, body, day]);

  return (
    <div className="notes-panel">
      <textarea
        className="daily-text"
        value={body}
        placeholder="What are you watching, and what would make you stand aside?"
        aria-label="Today’s note"
        onChange={(e) => { setBody(e.target.value); setDirty(true); }}
      />
      <div className="notes-foot">
        {error
          ? <span className="neg">{error}</span>
          : <span className="dim">{dirty ? 'Unsaved' : 'Saved to today’s prep note'}</span>}
        <button
          type="button"
          className="btn btn-primary compact"
          disabled={!dirty || busy}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
